import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Save, Trash2, Package, FileText, Globe, HelpCircle, ExternalLink, Image, Palette, Type, BookOpen, CopyPlus } from 'lucide-react';
import { getImageUrl } from '../utils/getImageUrl';
import { useNotification } from '../hooks/useNotification';

function EditBook() {
  const { bookId } = useParams();
  const { notify, Notification } = useNotification();

  const [bookName, setBookName] = useState('');
  const [bookSizeLabel, setBookSizeLabel] = useState('');
  const [currentCoverImage, setCurrentCoverImage] = useState('');
  const [newCoverFile, setNewCoverFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [frontCoverText, setFrontCoverText] = useState('');
  const [backCoverText, setBackCoverText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculatingSize, setCalculatingSize] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [designPresets, setDesignPresets] = useState([]);
  const [defaultPresetId, setDefaultPresetId] = useState('');

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const response = await axios.get(`/api/book/${bookId}`);
        const book = response.data;
        setBookName(book.name || '');
        setBookSizeLabel(book.size?.label || '');
        setCurrentCoverImage(book.cover_image || '');
        setDefaultPresetId(book.default_preset_id || '');

        // Fetch cover texts
        try {
          const coverTextsResponse = await axios.get(
            `/api/books/${bookId}/cover-texts`,
          );
          const coverTexts = coverTextsResponse.data.cover_texts;

          // Find English text for front and back covers
          const frontEnText =
            coverTexts?.front?.find((item) => item.language === 'en')?.text ||
            '';
          const backEnText =
            coverTexts?.back?.find((item) => item.language === 'en')?.text ||
            '';

          setFrontCoverText(frontEnText);
          setBackCoverText(backEnText);
        } catch (coverErr) {
          // Cover texts might not exist yet, that's okay
          console.log('Cover texts not found or error fetching them');
        }

        // Fetch book statistics
        try {
          const statsResponse = await axios.get(`/api/book/${bookId}/statistics`);
          setStatistics(statsResponse.data);
        } catch (statsErr) {
          console.log('Failed to fetch book statistics');
        }

        setLoading(false);
      } catch (err) {
        notify('Failed to load book', 'error');
        setLoading(false);
      }
    };

    fetchBook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  useEffect(() => {
    axios.get('/api/design-presets', { withCredentials: true })
      .then((res) => setDesignPresets(res.data))
      .catch(() => {});
  }, []);

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewCoverFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteCover = async () => {
    if (!window.confirm('Are you sure you want to delete the cover image?')) {
      return;
    }

    try {
      setSaving(true);
      await axios.delete(`/api/books/${bookId}/cover`);
      setCurrentCoverImage('');
      notify('Cover image deleted successfully');
    } catch (err) {
      notify('Failed to delete cover image', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCalculateSize = async () => {
    setCalculatingSize(true);

    try {
      const response = await axios.post(`/api/books/${bookId}/calculate-size`);
      setBookSizeLabel(response.data.size?.label || '');
      notify('Book size calculated successfully');
    } catch (err) {
      notify(
        err.response?.data?.error || 'Failed to calculate book size',
        'error',
      );
    } finally {
      setCalculatingSize(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!bookName) {
      notify('Book name is required', 'error');
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();
      formData.append('name', bookName);
      formData.append('default_preset_id', defaultPresetId || '');

      if (newCoverFile) {
        formData.append('cover', newCoverFile);
      }

      const response = await axios.patch(`/api/books/${bookId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Save front cover text
      if (frontCoverText !== undefined) {
        await axios.put(`/api/books/${bookId}/cover-texts/front/en`, {
          text: frontCoverText,
        });
      }

      // Save back cover text
      if (backCoverText !== undefined) {
        await axios.put(`/api/books/${bookId}/cover-texts/back/en`, {
          text: backCoverText,
        });
      }

      notify('Book updated successfully');
      setCurrentCoverImage(response.data.cover_image || currentCoverImage);
      setNewCoverFile(null);
      setPreviewUrl('');
    } catch (err) {
      notify('Failed to update book', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  // Helper to get language name
  const getLanguageName = (code) => {
    const names = {
      en: 'English',
      de: 'German',
      es: 'Spanish',
      nl: 'Dutch',
      ru: 'Russian',
      it: 'Italian',
      fr: 'French',
      pl: 'Polish',
      tr: 'Turkish'
    };
    return names[code] || code.toUpperCase();
  };

  // Tooltip component
  const Tooltip = ({ text, children }) => {
    const [show, setShow] = useState(false);

    return (
      <div className="relative inline-block">
        <div
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
        >
          {children}
        </div>
        {show && (
          <div className="absolute z-10 right-0 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded shadow-lg">
            {text}
            <div className="absolute -top-1 right-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
          </div>
        )}
      </div>
    );
  };

  Tooltip.propTypes = {
    text: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
  };

  return (
    <>
      <Notification />
      <div className="max-w-7xl mx-auto p-6 mt-10">
        <h2 className="text-3xl font-bold mb-6">Edit Book</h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form - Left Column (2/3 width) */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-4">

            {/* Section A: Basic Info */}
            <div className="bg-white shadow-sm rounded-lg border border-gray-100">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 bg-gray-50 rounded-t-lg">
                <BookOpen size={15} className="text-gray-500" />
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Basic Info
                </span>
              </div>
              <div className="px-5 py-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Book Name
                </label>
                <input
                  type="text"
                  value={bookName}
                  onChange={(e) => setBookName(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                  placeholder="Enter book name"
                  required
                />

                <div className="mt-4">
                  <label className="block text-gray-700 font-medium mb-2">
                    Size
                  </label>
                  <div className="w-full rounded border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700">
                    {bookSizeLabel || 'Not set yet'}
                  </div>
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={handleCalculateSize}
                      disabled={calculatingSize}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                      {calculatingSize ? 'Calculating...' : 'Calculate'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Section B: Cover Image */}
            <div className="bg-white shadow-sm rounded-lg border border-gray-100">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 bg-gray-50 rounded-t-lg">
                <Image size={15} className="text-gray-500" />
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Cover Image
                </span>
              </div>
              <div className="px-5 py-4 space-y-4">
                {currentCoverImage && !previewUrl && (
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Current Cover
                    </label>
                    <div className="relative inline-block">
                      <img
                        src={getImageUrl(currentCoverImage)}
                        alt="Cover"
                        className="w-full max-w-md mx-auto rounded border"
                      />
                      <button
                        type="button"
                        onClick={handleDeleteCover}
                        className="absolute top-2 right-2 p-2 bg-white/90 hover:bg-red-50 rounded-full shadow-sm border border-gray-200 hover:border-red-300 transition-colors group"
                        disabled={saving}
                        title="Remove cover image"
                      >
                        <Trash2 size={16} className="text-gray-600 group-hover:text-red-500" />
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    {currentCoverImage ? 'Replace Cover Image' : 'Upload Cover Image'}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageChange}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Accepted formats: JPG, PNG, GIF
                  </p>
                </div>
                {previewUrl && (
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Preview
                    </label>
                    <img
                      src={previewUrl}
                      alt="New cover preview"
                      className="w-full max-w-md mx-auto rounded border"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Section C: Design Settings */}
            <div className="bg-white shadow-sm rounded-lg border border-gray-100">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 bg-gray-50 rounded-t-lg">
                <Palette size={15} className="text-gray-500" />
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Design Settings
                </span>
              </div>
              <div className="px-5 py-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Default Design Preset
                </label>
                <select
                  value={defaultPresetId}
                  onChange={(e) => setDefaultPresetId(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="">— No default preset —</option>
                  {designPresets.map((preset) => (
                    <option key={preset._id} value={preset._id}>
                      {preset.name}{preset.is_default ? ' (global default)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  This preset will be pre-selected when editing pages of this book.
                </p>
              </div>
            </div>

            {/* Section D: Cover Texts */}
            <div className="bg-white shadow-sm rounded-lg border border-gray-100">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 bg-gray-50 rounded-t-lg">
                <Type size={15} className="text-gray-500" />
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Cover Texts
                </span>
                <span className="text-xs text-gray-400 font-normal normal-case tracking-normal ml-1">
                  (English)
                </span>
              </div>
              <div className="px-5 py-4 space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Front Cover
                  </label>
                  <textarea
                    value={frontCoverText}
                    onChange={(e) => setFrontCoverText(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Enter front cover text in English"
                    rows={4}
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Back Cover
                  </label>
                  <textarea
                    value={backCoverText}
                    onChange={(e) => setBackCoverText(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Enter back cover text in English"
                    rows={4}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-4 pt-2">
              <button
                type="submit"
                className="bg-gray-900 text-white px-6 py-2 rounded hover:bg-gray-800 disabled:bg-gray-400 flex items-center gap-2"
                disabled={saving}
              >
                <Save size={16} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <Link
                to="/books"
                className="bg-white border border-gray-300 px-6 py-2 rounded hover:bg-gray-50 inline-block"
              >
                Cancel
              </Link>
            </div>
          </form>

      {/* Statistics Panel - Right Column (1/3 width) */}
      <div className="space-y-6">
        {/* Quick Actions */}
        <div className="bg-white shadow-lg rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>

          <div className="space-y-2">
            <Link
              to={`/books/${bookId}/variants/new`}
              className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
            >
              <CopyPlus className="h-4 w-4" />
              <span>Create Variant</span>
            </Link>

            {statistics && (
              <Link
                to={`/pages/${bookId}?plang=es_de`}
                className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
              >
                <FileText className="h-4 w-4" />
                <span>Pages <span className="font-semibold">{statistics.total_pages}</span></span>
              </Link>
            )}

            <Link
              to={`/book-translations/${bookId}`}
              className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
            >
              <Globe className="h-4 w-4" />
              <span>Manage Translations</span>
            </Link>

            <Link
              to={`/book-releases/${bookId}`}
              className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
            >
              <Package className="h-4 w-4" />
              <span>View Releases</span>
            </Link>
          </div>
        </div>

        {/* Translation Status */}
        {statistics && statistics.translation_stats && (
          <div className="bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FileText size={20} />
                Translation Status
              </h3>
              <Tooltip text="Shows text translation completion. Only pages with English text are counted. For each language, it shows how many pages have been translated (text_{lang} field is not empty) out of total pages with English text.">
                <HelpCircle size={16} className="text-gray-400 hover:text-gray-600 cursor-help" />
              </Tooltip>
            </div>

            <div className="space-y-4">
              {Object.entries(statistics.translation_stats).map(([lang, stats]) => (
                <div key={lang}>
                  <div className="flex justify-between items-center mb-1">
                    <Link
                      to={`/book-translations/${bookId}?plang=${lang}`}
                      className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
                    >
                      <span>{getLanguageName(lang)}</span>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                    <span className="text-sm text-gray-600">
                      {stats.pages_with_translation}/{stats.pages_needing_translation}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${stats.completion_percentage}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.completion_percentage}% translated
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Page render status */}
        {statistics && statistics.preview_stats && (
          <div className="bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Globe size={20} />
                Page renders
              </h3>
              <Tooltip text="Shows saved page renders for language pairs that include DE, ES, or EN. Each count is the number of rendered pages out of pages with English text.">
                <HelpCircle size={16} className="text-gray-400 hover:text-gray-600 cursor-help" />
              </Tooltip>
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {Object.entries(statistics.preview_stats).map(([pair, stats]) => (
                <div key={pair}>
                  <div className="flex justify-between items-center mb-1">
                    <Link
                      to={`/pages/${bookId}?plang=${pair.toLowerCase()}`}
                      className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
                    >
                      <span>{pair.toUpperCase()}</span>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                    <span className="text-sm text-gray-600">
                      {stats.pages_with_preview}/{stats.pages_needing_preview}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${stats.completion_percentage}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.completion_percentage}% generated
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  </div>
    </>
  );
}

export default EditBook;
